import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Field, StructureType } from '../../types/fields';
import { FieldsPanelComponent } from './components/fields-panel.component';
import { StructuresPanelComponent } from './components/structures-panel.component';

@Component({
  selector: 'app-fields',
  imports: [
    FieldsPanelComponent,
    StructuresPanelComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="min-h-full bg-zinc-50/50 dark:bg-transparent p-4 sm:p-8">
      <div class="mx-auto max-w-[1600px] space-y-8">

        <!-- Header -->
        <div class="flex items-center justify-between">
          <div>
            <h1 data-testid="schema-management-heading" class="text-2xl font-bold tracking-tight text-zinc-900 dark:text-white">Schema Management</h1>
            <p data-testid="schema-management-subtitle" class="text-zinc-500 dark:text-zinc-400">Define fields, group them into structures — templates extract data based on them. Hover a structure to see its fields.</p>
          </div>
        </div>

        <!-- Content Area - Side by Side Grid -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">

          <!-- LEFT COLUMN: FIELDS -->
          <app-fields-panel
            (fieldsChanged)="onFieldsChanged($event)"
            [autoEditFieldId]="editFieldId()"
            [hoveredStructure]="hoveredStructure()"
          />

          <!-- RIGHT COLUMN: STRUCTURES -->
          <app-structures-panel
            [allFields]="fields()"
            [autoEditStructureId]="editStructureId()"
            (structureHovered)="hoveredStructure.set($event)"
          />

        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      height: 100%;
    }
  `]
})
export class FieldsComponent {
  private route = inject(ActivatedRoute);
  fields = signal<Field[]>([]);
  editStructureId = signal<string | null>(null);
  editFieldId = signal<string | null>(null);
  /** Structure hovered in the right panel — drives field highlighting in the left panel. */
  hoveredStructure = signal<StructureType | null>(null);

  constructor() {
    this.route.queryParams.subscribe(params => {
      this.editStructureId.set(params['structureId'] || null);
      this.editFieldId.set(params['fieldId'] || null);
    });
  }

  onFieldsChanged(fields: Field[]): void {
    this.fields.set(fields);
  }
}
